import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

export type StaffRow = {
  id: string;
  name: string;
  staffCode: string | null;
  memberCode: string | null;
  email: string | null;
  mobile: string | null;
  isRegistered: boolean;
  isActive: boolean;
  hasDuitNowQr: boolean;
  qrSharedCount: number;
  surveyCount: number;
};

export type UploadStaffResult = {
  ok: boolean;
  created?: number;
  skipped?: number;
  total?: number;
  errors?: string[];
  error?: string;
};

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  };
}

async function parseJsonError(res: Response, fallback: string): Promise<never> {
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(data.error || fallback);
}

export async function fetchStaffList(): Promise<StaffRow[]> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    staff?: StaffRow[];
    error?: string;
  };

  if (!res.ok || !data.ok || !Array.isArray(data.staff)) {
    throw new Error(data.error || 'Failed to load ambassadors');
  }

  return data.staff;
}

export async function fetchStaffById(id: string): Promise<StaffRow> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    staff?: StaffRow;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.staff) {
    await parseJsonError(res, data.error || 'Failed to load ambassador');
  }

  return data.staff!;
}

export async function fetchStaffDuitNowQrBlob(id: string): Promise<Blob> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff/${encodeURIComponent(id)}/duitnow-qr`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    await parseJsonError(res, 'Failed to load DuitNow QR');
  }
  return res.blob();
}

export async function uploadStaffDuitNowQr(id: string, file: File): Promise<void> {
  const image = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });

  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff/${encodeURIComponent(id)}/duitnow-qr`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ image }),
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };

  if (!res.ok || !data.ok) {
    throw new Error(data.error || 'Failed to upload DuitNow QR');
  }
}

export async function deleteStaffDuitNowQr(id: string): Promise<void> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff/${encodeURIComponent(id)}/duitnow-qr`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };

  if (!res.ok || !data.ok) {
    throw new Error(data.error || 'Failed to remove DuitNow QR');
  }
}

export async function updateStaff(
  id: string,
  input: {
    name: string;
    mobile?: string;
    email?: string;
    isActive?: boolean;
  }
): Promise<StaffRow> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    staff?: StaffRow;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.staff) {
    await parseJsonError(res, data.error || 'Failed to update ambassador');
  }

  return data.staff!;
}

export async function deleteStaff(id: string): Promise<void> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };

  if (!res.ok || !data.ok) {
    await parseJsonError(res, data.error || 'Failed to delete ambassador');
  }
}

export async function uploadStaffCsv(csv: string): Promise<UploadStaffResult> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ csv }),
  });
  const data = (await res.json().catch(() => ({}))) as UploadStaffResult;

  if (!res.ok || !data.ok) {
    throw new Error(data.error || 'CSV upload failed');
  }

  return data;
}

export type StaffPerformance = {
  totalSurveys: number;
  uniqueConsumers: number;
  qrShared: number;
  conversionRate: number;
  totalPoints: number | null;
  changes: {
    totalSurveys: number;
    uniqueConsumers: number;
    qrShared: number;
    conversionRate: number;
  };
  topState: string | null;
  surveyTrend: Array<{ date: string; count: number }>;
  topBrands: Array<{ brand: string; count: number }>;
  recentActivity: Array<{ type: string; label: string; at: string }>;
  weeklyPerformance: Array<{ week: string; surveys: number; qrScans: number }>;
};

export async function fetchStaffPerformance(
  id: string,
  input: { startISO: string; endISO: string }
): Promise<{ staff: StaffRow; performance: StaffPerformance }> {
  const apiUrl = getApiOrigin();
  const params = new URLSearchParams({
    startDate: input.startISO,
    endDate: input.endISO,
  });

  const res = await fetch(
    `${apiUrl}/api/v1/admin/staff/${encodeURIComponent(id)}/performance?${params.toString()}`,
    { headers: { Authorization: `Bearer ${getToken()}` } }
  );

  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    staff?: StaffRow;
    performance?: StaffPerformance;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.staff || !data.performance) {
    throw new Error(data.error || 'Failed to load ambassador performance');
  }

  return { staff: data.staff, performance: data.performance };
}

export async function createStaff(input: {
  name: string;
  email: string;
  mobile?: string;
}): Promise<void> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/staff`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };

  if (!res.ok || !data.ok) {
    throw new Error(data.error || 'Failed to create ambassador');
  }
}
